<template>
  <div style="height: 100%; width: 100%" :style="cssVars">
    <q-table
      :dark="headerDark"
      v-if="selection == 'multiple'"
      ref="table"
      style="width: 100%"
      :grid-header="false"
      :square="square"
      :data="data"
      :columns="columns"
      :separator="separator"
      :rows-per-page-options="[0]"
      :pagination.sync="syncPagination"
      :flat="flat"
      :grid="grid"
      :hide-bottom="hideBottom"
      :filter="filter"
      :filter-method="customFilter"
      virtual-scroll
      :row-key="rowKey"
      @row-click="handleClick"
      @row-dblclick="handleDoubleClick"
      :selected.sync="selected"
      :selection="selection"
      @selection="onSelection"
      :class="{
        sticky: sticky,
      }"
    >
      <!--HEADER TEMPLATE-->

      <template v-slot:header="props" v-if="isCustomHeader">
        <slot :name="`header`" v-bind:props="props">
          <q-tr :props="props">
            <slot
              v-for="col in props.cols"
              :name="`header-cell-${col.name}`"
              v-bind:props="props"
            >
              <q-th :key="col.name" :props="props">
                {{ col.label }}
              </q-th>
            </slot>
          </q-tr>
        </slot>
      </template>

      <template
        v-for="column in columns"
        v-slot:[`header-cell-${column.name}`]="props"
        v-else
      >
        <slot :name="`header-cell-${column.name}`" v-bind:props="props">
          <q-th :props="props" :key="column.name">
            <span
              :class="{
                columnLabel: !column.boldHeader,
                columnLabelBold: column.boldHeader,
                columnCaps: column.capsHeader,
              }"
              :style="column.headerTextStyle ? column.headerTextStyle : ''"
            >
              {{ column.label }}
            </span>
          </q-th>
        </slot>
      </template>

      <!--GRID STYLE BODY TEMPLATE-->
      <template v-slot:item="props" v-if="grid">
        <div class="q-pa-sm col-xl-2 col-lg-3 col-md-4 col-xs-12">
          <slot :name="`body-grid`" v-bind:props="props"></slot>
        </div>
      </template>

      <template v-if="updateBody" v-slot:body="props">
        <slot :name="`body`" v-bind:props="props">
          <q-tr
            @click="handleClick(null, props.row)"
            @dblclick="handleDoubleClick(null, props.row)"
            :props="props"
          >
            <slot
              v-for="column in props.cols"
              :name="`body-cell-${column.name}`"
              v-bind:props="props"
            >
              <q-td :key="column.name" :props="props">
                {{ column.value }}
              </q-td>
            </slot>
          </q-tr>
        </slot>
      </template>
      <template
        v-else
        v-for="column in columns"
        v-slot:[`body-cell-${column.name}`]="props"
      >
        <slot :name="`body-cell-${column.name}`" v-bind:props="props">
          <q-td :props="props">
            {{ props.value }}
          </q-td>
        </slot>
      </template>
    </q-table>
    <q-table
      v-else
      ref="table"
      style="width: 100%"
      :square="square"
      :data="data"
      :columns="columns"
      :separator="separator"
      :rows-per-page-options="[0]"
      :pagination.sync="syncPagination"
      :flat="flat"
      :grid="grid"
      :filter="filter"
      :filter-method="customFilter"
      virtual-scroll
      :row-key="rowKey"
      @row-click="handleClick"
      @row-dblclick="handleDoubleClick"
      :selected.sync="selected"
      :class="{
        sticky: sticky,
      }"
      :hide-bottom="hideBottom"
    >
      <!--HEADER TEMPLATE-->
      <template v-slot:header="props" v-if="isCustomHeader">
        <slot :name="`header`" v-bind:props="props">
          <q-tr :props="props">
            <slot
              v-for="col in props.cols"
              :name="`header-cell-${col.name}`"
              v-bind:props="props"
            >
              <q-th :key="col.name" :props="props">
                {{ col.label }}
              </q-th>
            </slot>
          </q-tr>
        </slot>
      </template>

      <template
        v-for="column in columns"
        v-slot:[`header-cell-${column.name}`]="props"
        v-else
      >
        <slot :name="`header-cell-${column.name}`" v-bind:props="props">
          <q-th
            :props="props"
            :style="
              column.sticky ? stickyHeaderCSS(column.sticky) : 'z-index: 80;'
            "
          >
            <span
              :class="{
                columnLabel: !column.boldHeader,
                columnLabelBold: column.boldHeader,
                columnCaps: column.capsHeader,
              }"
              :style="column.headerTextStyle ? column.headerTextStyle : ''"
            >
              {{ column.label }}
            </span>
          </q-th>
        </slot>
      </template>

      <!--GRID STYLE BODY TEMPLATE-->
      <template v-slot:item="props" v-if="grid">
        <div class="q-pa-xs col-lg-3 col-md-4 col-xs-12">
          <slot :name="`body-grid`" v-bind:props="props"></slot>
        </div>
      </template>

      <!--LIST STYLE TEMPLATE-->
      <template v-if="updateBody" v-slot:body="props">
        <slot :name="`body`" v-bind:props="props">
          <q-tr
            @click="handleClick(null, props.row)"
            @dblclick="handleDoubleClick(null, props.row)"
            :props="props"
          >
            <slot
              v-for="column in props.cols"
              :name="`body-cell-${column.name}`"
              v-bind:props="props"
            >
              <q-td :key="column.name" :props="props">
                {{ column.value }}
              </q-td>
            </slot>
          </q-tr>
        </slot>
      </template>
      <template
        v-else
        v-for="column in columns"
        v-slot:[`body-cell-${column.name}`]="props"
      >
        <slot :name="`body-cell-${column.name}`" v-bind:props="props">
          <q-td :props="props">
            <span :style="cellBodyStyle">
              {{ props.value }}
            </span>
          </q-td>
        </slot>
      </template>
    </q-table>
  </div>
</template>

<script>
  import { mapGetters } from "vuex";
  import moment from "moment";

  export default {
    components: {},
    computed: {
      cssVars() {
        return {
          "--header-bg-color": this.headerBgColor,
          "--header-text-color": this.headerTextColor,
          "--body-hover-color": this.bodyHoverColor,
          "--body-alt-color": this.bodyAltColor,
          "--sticky-column-bg-color": this.stickyColumnBgColor,
          "--body-selected-color": this.bodySelectedColor,
          width: "100%",
        };
      },
    },
    props: {
      columns: Array,
      data: Array,
      rowKey: {
        type: String,
        default: "id",
      },
      flat: {
        type: Boolean,
        default: false,
      },
      separator: {
        type: String,
        default: "horizontal",
      },
      sticky: {
        type: Boolean,
        default: true,
      },
      filter: {
        default: "",
      },
      externalFilter: {
        type: Function,
      },
      selection: {
        type: String,
        default: "single",
      },
      updateBody: {
        type: Boolean,
        default: false,
      },
      headerBgColor: {
        type: String,
        default: "white",
      },
      headerTextColor: {
        type: String,
        default: "black",
      },
      bodyHoverColor: {
        type: String,
      },
      bodyAltColor: {
        type: String,
        default: "",
      },
      square: {
        type: Boolean,
        default: false,
      },
      headerDark: {
        type: Boolean,
        default: false,
      },
      bodyDark: {
        type: Boolean,
        default: false,
      },
      grid: {
        type: Boolean,
        default: false,
      },
      stickyColumnBgColor: {
        type: String,
        default: "black",
      },
      isCustomHeader: {
        type: Boolean,
        default: false,
      },
      hideBottom: {
        type: Boolean,
        default: false,
      },
      tickOnClick: {
        type: Boolean,
        default: true,
      },
      gridHeader: {
        type: Boolean,
        default: true,
      },
      cellBodyStyle: {
        type: String,
        default: "",
      },
      bodySelectedColor: {
        type: String,
        default: "#ededed",
      },
      pagination: {
        default: null,
      },
    },
    watch: {
      selected: {
        handler(value) {
          this.$emit("onSelected", value);
        },
        deep: true,
      },
    },
    mounted() {
      this.syncPagination = this.pagination
        ? JSON.parse(JSON.stringify(this.pagination))
        : this.syncPagination;
    },
    data: () => ({
      syncPagination: {
        rowsPerPage: 0,
      },
      selected: [],
      lastIndex: null,
    }),
    methods: {
      handleClick(e, row) {
        if (this.tickOnClick) {
          if (this.selection == "single") {
            this.selected = [row];
          } else {
            let index = this.selected.findIndex((e) => row.id == e.id);
            if (index > -1) {
              this.selected.splice(index, 1);
            } else {
              this.selected.push(row);
            }
          }
        }
        this.$emit("rowSelected", row);
      },
      handleDoubleClick(e, row) {
        this.$emit("rowDoubleClick", row);
      },

      customFilter(rows, terms, cols) {
        // If terms is a straight string then search as normal Otherwise use externalFilter()
        if (typeof terms == "string") {
          return rows.filter((row) => {
            let matchFound = false;
            for (let i = 0; i < cols.length; i++) {
              let val = `${row[cols[i].field]}`.toLowerCase();
              matchFound = val.toLowerCase().indexOf(terms.toLowerCase()) > -1;

              if (matchFound == true) {
                i = cols.length;
              }
            }
            return matchFound;
          });
        } else {
          // External Filter is sent through as a Property
          return this.externalFilter(rows, terms, cols);
        }
      },
      selectAll(selectAll) {
        if (selectAll) {
          this.selected = [...this.data];
        } else {
          this.selected = [];
        }
      },

      stickyHeaderCSS(direction) {
        return `background-color:${
          this.cssVars["--header-bg-color"]
        };position:sticky;${
          direction == "left" ? "left: 0" : "right: 0"
        };z-index: 100;`;
      },
      stickyColumnCSS(direction) {
        return `background-color:${
          this.cssVars["--sticky-column-bg-color"]
        };position:sticky;${
          direction == "left" ? "left: 0" : "right: 0"
        };z-index: 1;`;
      },
      onSelection({ rows, added }) {
        let evt = window.event;

        if (rows.length === 0 || this.$refs.table === void 0) {
          return;
        }

        const row = rows[0];
        const filteredSortedRows = this.$refs.table.filteredSortedRows;
        const rowIndex = filteredSortedRows.indexOf(row);
        const lastIndex = this.lastIndex;

        this.lastIndex = rowIndex;
        document.getSelection().removeAllRanges();

        if (this.$q.platform.is.mobile === true) {
          evt = { ctrlKey: true };
        } else if (
          evt !== Object(evt) ||
          (evt.shiftKey !== true && evt.ctrlKey !== true)
        ) {
          this.selected = added === true ? rows : [];

          return;
        }

        const operateSelection =
          added === true
            ? (selRow) => {
                const selectedIndex = this.selected.indexOf(selRow);
                if (selectedIndex === -1) {
                  this.selected = this.selected.concat(selRow);
                }
              }
            : (selRow) => {
                const selectedIndex = this.selected.indexOf(selRow);
                if (selectedIndex > -1) {
                  this.selected = this.selected
                    .slice(0, selectedIndex)
                    .concat(this.selected.slice(selectedIndex + 1));
                }
              };
        if (lastIndex === null || evt.shiftKey !== true) {
          operateSelection(row);

          return;
        }
        const from = lastIndex < rowIndex ? lastIndex : rowIndex;
        const to = lastIndex < rowIndex ? rowIndex : lastIndex;

        for (let i = from; i <= to; i += 1) {
          operateSelection(filteredSortedRows[i]);
        }
        let test = JSON.parse(JSON.stringify(this.selected));
        setTimeout(() => {
          this.selected = test;
        }, 50);
      },
    },
  };
</script>



<style lang="scss">
  .sticky {
    background-color: white !important;
    height: 100% !important;

    .q-table__bottom,
    thead tr:first-child th {
      /* bg color is important for th; just specify one */
      background-color: white;
      z-index: 300;
      color: black;
    }
    // thead tr {
    //   z-index: 10000;
    //   background: white;
    // }

    .q-table__top,
    thead tr:first-child th {
      /* bg color is important for th; just specify one */
      background-color: var(--header-bg-color);
      color: var(--header-text-color) !important;
    }

    thead tr th {
      position: sticky;
    }

    thead tr:first-child th {
      top: 0;
    }

    /* this is when the loading indicator appears */
    &.q-table--loading thead tr:last-child th {
      top: 48px;
      /* height of all previous header rows */
    }

    tr:nth-child(even) {
      background-color: var(--body-alt-color) !important;
    }

    tr:hover {
      background-color: var(--body-hover-color) !important;
    }
    // td:nth-child(2) {
    //   background-color: #fff;
    //   position: sticky;
    //   left: 0;
    //   z-index: 1;
    // }

    // th:nth-child(2) {
    //   background-color: var(--header-bg-color);
    //   position: sticky;
    //   left: 0;
    //   z-index: 100;
    // }
  }

  #solutionsTable .q-table {
    border-spacing: 0 0.5rem;
    background-color: white;

    margin-top: -1em;

    thead tr {
      z-index: 1;
    }

    tr td:first-child {
      border-bottom-left-radius: 9px;
      border-top-left-radius: 9px;
    }
    tr td:last-child {
      border-bottom-right-radius: 9px;
      border-top-right-radius: 9px;
    }

    tr {
      position: relative;
      transform: scale(1);
      transition: 0.3s;
    }
  }

  .columnLabel {
    font-family: "Montserrat";
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    line-height: 13px;
  }

  .columnLabelBold {
    font-family: "Montserrat";
    font-style: normal;
    font-weight: 700;
    font-size: 14px;
    line-height: 17px;
    letter-spacing: -0.02em;
  }

  .columnCaps {
    text-transform: uppercase;
  }

  .rowLabel {
    font-family: "Montserrat";
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 17px;
    display: flex;
    align-items: center;
    letter-spacing: -0.02em;
    color: black;
  }

  .rowLabelBold {
    font-family: "Montserrat";
    font-style: normal;
    font-weight: 700;
    font-size: 14px;
    line-height: 13px;
    display: flex;
    align-items: center;
    color: #162c47;
  }

  .card-title {
    font-family: "Montserrat";
    font-style: normal;
    font-weight: 700;
    font-size: 14px;
    line-height: 17px;
  }
</style>
